import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import http from 'helpers/requests';
import { cgToast } from 'modules/Common/ui/toast';
import { useBranding } from 'modules/App/hooks/useBranding';

/**
 * Hook for completing the onboarding wizard
 *
 * POST /api/onboarding/complete
 *
 * @param {Object} options - Optional callbacks
 * @param {Function} options.onSuccess - Custom success callback
 * @param {Function} options.onError - Custom error callback
 *
 * @returns {Object} Mutation object with mutate, mutateAsync, isLoading, error, data
 *
 * @example
 * const { mutate, isLoading } = useCompleteOnboarding({
 *   onSuccess: (data) => {
 *     history.push('/overview');
 *   }
 * });
 *
 * mutate({
 *   organizationType: 'declarant',
 *   organizationName: 'Acme Corp',
 *   organizationSize: '51-200',
 *   // ... other fields
 * });
 */
const useCompleteOnboarding = (options = {}) => {
  const { onSuccess: customOnSuccess, onError: customOnError } = options;
  const queryClient = useQueryClient();
  const brand = useBranding();
  const { t } = useTranslation('onboarding');

  return useMutation({
    mutationFn: async (onboardingData) => {
      const response = await http.post('/onboarding/complete', onboardingData);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['onboardingCheck'] });
      queryClient.invalidateQueries({ queryKey: ['onboarding', 'check'] });
      queryClient.invalidateQueries({ queryKey: ['trialStatus'] });

      // Show success notification
      cgToast.success(t('common:toasts.success', 'Success'), {
        description: t(
          'onboarding:wizard.accountCreatedWelcome',
          'Account created successfully! Welcome to {{brandName}}.',
          {
            brandName: brand.name,
          },
        ),
      });

      // Call custom onSuccess if provided
      if (customOnSuccess) {
        customOnSuccess(data);
      }
    },
    onError: (error) => {
      // Handle specific error cases
      if (error.response?.status === 409) {
        cgToast.info(t('common:toasts.info', 'Info'), {
          description: t(
            'onboarding:wizard.alreadyHaveOrgRedirect',
            'You already have an organization. Redirecting to dashboard...',
          ),
        });
      } else {
        cgToast.apiError(error);
      }

      // Call custom onError if provided
      if (customOnError) {
        customOnError(error);
      }
    },
  });
};

export default useCompleteOnboarding;
